import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Table } from '../../../../../common/ui';
import styles from './styles.module.css';

const toLocalDate = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [year, month, day] = value.slice(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toApiDate = (date) => {
  const localDate = toLocalDate(date);
  if (!localDate) return null;
  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, '0');
  const day = String(localDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const AgreementTable = ({
  rows,
  totalCount,
  page,
  rowsPerPage,
  isLoading,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const columns = useMemo(
    () => [
      { id: 'agreementNo', label: 'Agreement No' },
      { id: 'customer', label: 'Customer' },
      { id: 'planType', label: 'Plan Type' },
      {
        id: 'depositAmount',
        label: 'Deposit Amount',
        format: (value) => Number(value || 0).toFixed(2),
      },
      {
        id: 'financedPrincipal',
        label: 'Financed Principal',
        format: (value) => Number(value || 0).toFixed(2),
      },
      {
        id: 'remainingBalance',
        label: 'Remaining Balance',
        format: (value) => Number(value || 0).toFixed(2),
      },
      {
        id: 'overdueAmount',
        label: 'Overdue Amount',
        format: (value) => Number(value || 0).toFixed(2),
      },
      {
        id: 'nextDueDate',
        label: 'Next Due Date',
        format: (value) => toApiDate(value) || '-',
      },
      {
        id: 'startDate',
        label: 'Start Date',
        format: (value) => toApiDate(value) || '-',
      },
      { id: 'status', label: 'Status' },
      {
        id: 'progressBar',
        label: 'Progress Bar',
        minWidth: 140,
        render: (value) => {
          const percent = Math.min(100, Math.max(0, Number(value) || 0));
          return (
            <div className={styles.progressCell}>
              <div className={styles.progressTrack}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${percent}%` }}
                />
              </div>
              <span className={styles.progressLabel}>{percent.toFixed(0)}%</span>
            </div>
          );
        },
      },
    ],
    []
  );

  if (isLoading) {
    return <div className={styles.loading}>Loading agreements...</div>;
  }

  return (
    <Table
      columns={columns}
      rows={rows}
      page={page}
      rowsPerPage={rowsPerPage}
      totalCount={totalCount}
      disableInternalPagination
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      emptyMessage="No agreements found."
    />
  );
};

AgreementTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  totalCount: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
};

export default AgreementTable;
